import { registerLocaleData } from '@angular/common';
import localeEnUS from '@angular/common/locales/en';
// Import the specific locales we want to support
import localeEnGB from '@angular/common/locales/en-GB';
import localeEnIN from '@angular/common/locales/en-IN';
import { enableProdMode, LOCALE_ID } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';

import { registerLicense, setCulture } from '@syncfusion/ej2-base';

import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';
import { STORAGE_KEY_VERSION, STORAGE_VERSION } from './app/common/util/constants';
import { environment } from './environments/environment';

/**
 * AK-538: on-demand localStorage reset via a storage version. Runs BEFORE
 * bootstrapApplication so it executes ahead of Angular/OIDC ever reading storage.
 * If the stored version does not match the current STORAGE_VERSION, wipe
 * localStorage and record the new version — giving returning users a clean slate
 * (purges stale OIDC blobs / expired Bearer tokens from the JWT era for the
 * cookie-session cutover). Matching version → no-op. Fails open: any storage error
 * must never block bootstrap.
 */
function resetLocalStorageIfVersionChanged(): void {
  try {
    if (localStorage.getItem(STORAGE_KEY_VERSION) === STORAGE_VERSION) {
      return;
    }
    localStorage.clear();
    localStorage.setItem(STORAGE_KEY_VERSION, STORAGE_VERSION);
    console.warn(`[storage-version] localStorage cleared for version "${STORAGE_VERSION}"`);
  } catch (err) {
    console.warn('[storage-version] could not evaluate/reset localStorage — continuing', err);
  }
}

resetLocalStorageIfVersionChanged();

if (environment.production) {
  enableProdMode();
}

registerLicense('Ngo9BigBOggjHTQxAR8/V1NMaF5cXmBCf1FpRmJGdld5fUVHYVZUTXxaS00DNHVRdkdmWX1fd3RRQmhZU013WUI=');
// eslint-disable-next-line
const supportedLocales: { [key: string]: any } = {
  'en-US': localeEnUS,
  'en-GB': localeEnGB,
  'en-IN': localeEnIN,
};

// Function to register locale data
const loadLocaleData = (locale: string) => {
  if (locale in supportedLocales) {
    registerLocaleData(supportedLocales[locale], locale);
  } else {
    // If the exact locale is not supported, try to find a match based on the language
    const language = locale.split('-')[0];
    const fallbackLocale = Object.keys(supportedLocales).find((key) => key.startsWith(language));

    if (fallbackLocale) {
      console.warn(`Exact locale ${locale} not available, falling back to ${fallbackLocale}`);
      registerLocaleData(supportedLocales[fallbackLocale], fallbackLocale);
      return fallbackLocale;
    } else {
      console.warn(`No matching locale found for ${locale}, falling back to en-GB`);
      registerLocaleData(supportedLocales['en-GB'], 'en-GB');
      return 'en-GB';
    }
  }

  return locale;
};

// Get the user's full locale
const userLocale = navigator.language;

// Load and register the locale data, then bootstrap the application
const effectiveLocale = loadLocaleData(userLocale);

// setting syncfusion locale
setCulture(userLocale);

bootstrapApplication(AppComponent, {
  ...appConfig,
  providers: [...appConfig.providers, { provide: LOCALE_ID, useValue: effectiveLocale }],
}).catch((err) => console.error(err));

// SSR is disabled for now.
// to enable, add the following to angular.json,
// under projects.knowledge.architect.build.options:
/*
  "server": "src/main.server.ts",
  "outputMode": "server", // currently set to "static"
  "ssr": {
    "entry": "src/server.ts"
  },
*/
